// Approved #3 is the order; #4 is this same order with PaymentDialog open.
// Source: approved-stack-2026-10-12/03-Build-An-Order-Consistency-Review-v8.html.
//
// R9 companion-state wiring: the ProductWorkspace + OrderSummary pair is
// wrapped in the shared DataBoundary. Internal signals (quoteError from
// the preview-quote fetch) convert to an error state honestly; external
// signals (loading, empty catalog, degraded aggregator, feature-gate
// off, active-call) arrive via companionSignals so tests and the
// runtime inject real values without this file branching on them. The
// Default state visual is unchanged.
//
// W32 (2026-10-20) real-contact wiring: on a real HubSpot session
// (useReviewIdentity().isPreview === false) the breadcrumb, hero,
// identity row and order summary use the real profile, the shipping
// address is seeded from GET /v1/contacts/:id/addresses, the basket
// starts empty (never Sarah's basket). SARAH / MICHAEL /
// DEFAULT_REVIEW_ADDRESS are reachable ONLY in preview.
//
// W36 (2026-10-20) pricing engine: on a real session every price on the
// screen is the engine's (POST /v1/pricing/calculate through
// enginePricing.ts / data/livePricing.ts): tile prices from the catalog
// quote, basket lines, tier, the 20% subscription line, the bundle line
// and the products subtotal from the basket quote. Tax and total stay
// "Pending Shopify tax". While the engine answers 503
// PRICING_RESOLVER_NOT_CONFIGURED (token not bound) the price block keeps
// the honest "Prices pending the pricing engine" line and Change stays
// inert. Once the engine has quoted the catalog, Change opens the
// payment dialog shell (design labels verbatim, every entry control
// disabled; the "4242" example never renders on a real session) and
// "Prepare secure payment link" is live only when its gate is ON.
// "Process order" stays a guarded no-charge control behind
// LIVE_WRITE_GATE_SHOPIFY_CREATE_DRAFT_ORDER. The design preview keeps
// the example table from data/mockPricing.ts; no fixture price is
// reachable on a real session.
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { WorkspaceChrome } from '../_shared/WorkspaceChrome.js';
import { PriorityFocus } from '../_shared/PriorityFocus.js';
import { IdentityRow } from '../_shared/IdentityRow.js';
import { CustomerTabs } from '../_shared/CustomerTabs.js';
import { FileShortcuts } from '../_shared/FileShortcuts.js';
import { UpNext } from '../_shared/UpNext.js';
import { ActionFooter } from '../_shared/ActionFooter.js';
import { useReviewState } from '../_shared/ReviewState.js';
import { DataBoundary } from '../_shared/DataBoundary.js';
import {
  resolveCompanionState,
  type ScreenCompanionSignals,
} from '../_shared/useScreenCompanionState.js';
import { useActiveCallSignal, mergeMidCallSignal } from '../_shared/useActiveCallSignal.js';
import {
  useContactAddresses,
  useReviewIdentity,
  type ContactAddressRecord,
  type ContactAddressesResult,
  type ReviewIdentity,
} from '../_shared/useReviewIdentity.js';
import { ProductWorkspace, type TierStrip, type TilePrices } from './components/ProductWorkspace.js';
import {
  OrderSummary, PRICES_PENDING_LABEL, type EngineSummary, type ExamplePrices, type OrderAddressStatus, type ShippingAddress,
} from './components/OrderSummary.js';
import { OrderDialog } from './components/OrderDialog.js';
import { PaymentDialog } from './components/PaymentDialog.js';
import { CATALOG_PRODUCTS, type CategoryKey } from '../../data/productCatalog.js';
import { priceEntryFor } from '../../data/mockPricing.js';
import { formatCents, tierLabel, tierSourceLabel } from '../../data/livePricing.js';
import { SARAH } from '../../data/mockData.js';
import {
  DEFAULT_REVIEW_ADDRESS, emptyAgreement, initialPayment, paymentAgreementKey,
  paymentLabel, validPreviewQuote, type OrderDraft, type PreviewQuote,
  type PreviewQuoteLoader, type PreviewQuoteRequest,
} from './orderState.js';
import {
  DRAFT_ORDER_GATE, SECURE_LINK_GATE, prepareSecureLink, purchaseTypeFor, useEnginePricing, useSecureLinkGate,
  type SecureLinkOutcome,
} from './enginePricing.js';
import './styles.css';
import './app.css';
import './approved-payment.css';

type DialogMode = 'closed' | 'address' | 'process' | 'secure-link' | 'massager' | 'laser';

export interface BuildOrderReviewScreenProps {
  loadPreviewQuote?: PreviewQuoteLoader;
  workId?: string;
  /** R9 test seam for the six companion states. */
  companionSignals?: ScreenCompanionSignals;
}

/** Where the order draft in review state came from (W32). */
interface OrderOrigin {
  draft: 'fixture' | 'real';
  address: 'pending' | 'hubspot' | 'none' | 'error';
}

const BLANK_ADDRESS: ShippingAddress = { recipient: '', street: '', line2: '', city: '', region: '', postal: '', country: '' };

// Design-preview example prices (data/mockPricing.ts), keyed by product
// name. Built once; only read when isPreview.
const EXAMPLE_PRICES: ExamplePrices = Object.fromEntries(
  CATALOG_PRODUCTS.map((p) => {
    const entry = priceEntryFor(p.name);
    return [p.name, entry ? { oneTime: entry.oneTime, subscription: entry.subscription } : undefined];
  }),
);
const EXAMPLE_SKUS: Record<string, string> = Object.fromEntries(
  CATALOG_PRODUCTS.map((p) => [p.name, priceEntryFor(p.name)?.sku ?? p.handle]),
);

function sameAddress(a: ShippingAddress, b: ShippingAddress): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Map one GET /v1/contacts/:id/addresses record (MEASURED 2026-10-20) onto the order address. */
export function shippingAddressFromRecord(record: ContactAddressRecord, recipient: string): ShippingAddress {
  return {
    recipient,
    street: record.line1 ?? '',
    line2: record.line2 ?? '',
    city: record.city ?? '',
    region: record.state ?? '',
    postal: record.zip ?? '',
    country: record.country ?? '',
  };
}

export function BuildOrderReviewScreen(props: BuildOrderReviewScreenProps = {}): ReactNode {
  const params = useParams();
  const location = useLocation();
  const customerId = params.customerId || SARAH.id;
  const workId = props.workId ?? (typeof location.state?.workId === 'string' ? location.state.workId : 'design-order');
  const identity = useReviewIdentity(customerId);
  const addresses = useContactAddresses(customerId);
  // W36: the design-preview quote comparator is only ever injected by
  // tests (props.loadPreviewQuote). Real sessions price through the
  // engine hook inside BuildOrderWorkspace; the old GET loader against
  // the POST-only route is gone.
  return <BuildOrderWorkspace key={customerId + ':' + workId} customerId={customerId} workId={workId}
    loadPreviewQuote={props.loadPreviewQuote} initialPaymentOpen={location.state?.openPayment === true}
    companionSignals={props.companionSignals} identity={identity} addresses={addresses} />;
}

function BuildOrderWorkspace({ customerId, workId, loadPreviewQuote, initialPaymentOpen, companionSignals, identity, addresses }: {
  customerId: string; workId: string; loadPreviewQuote?: PreviewQuoteLoader; initialPaymentOpen: boolean;
  companionSignals?: ScreenCompanionSignals; identity: ReviewIdentity; addresses: ContactAddressesResult;
}): ReactNode {
  const isPreview = identity.isPreview;
  const firstName = identity.firstName;
  const customer = identity.customer;
  const [draft, setDraft] = useReviewState<OrderDraft>(customerId, 'order:' + workId, () => ({
    quantities: Object.fromEntries(CATALOG_PRODUCTS.map((p) => [p.name, p.initialQty])),
    subscription: true, frequencyDays: '60', address: { ...DEFAULT_REVIEW_ADDRESS },
    addressConfirmed: false, revision: 1,
  }));
  const [origin, setOrigin] = useReviewState<OrderOrigin>(customerId, 'order-origin:' + workId, () => ({ draft: 'fixture', address: 'pending' }));
  const [payment, setPayment] = useReviewState(customerId, 'order-payment:' + workId, initialPayment);
  const [category, setCategory] = useReviewState<CategoryKey>(customerId, 'order-category:' + workId, () => 'Equine');
  const [dialog, setDialog] = useState<DialogMode>('closed');
  const [paymentOpen, setPaymentOpen] = useState(initialPaymentOpen);
  const [addressDraft, setAddressDraft] = useState<ShippingAddress>(draft.address);
  const [quoteResult, setQuoteResult] = useState<PreviewQuote | null>(null);
  const [quoteError, setQuoteError] = useState(false);
  const [linkOutcome, setLinkOutcome] = useState<SecureLinkOutcome | null>(null);

  // W32: the HubSpot default address, once the profile name and the
  // addresses read are both in.
  const hubspotAddress = useMemo<ShippingAddress | null>(() => {
    if (isPreview || addresses.status !== 'ready' || !addresses.items[0]) return null;
    return shippingAddressFromRecord(addresses.items[0], identity.status === 'ready' ? customer.fullName : '');
  }, [isPreview, addresses.status, addresses.items, identity.status, customer.fullName]);

  // W32: on a real session the fixture draft (Sarah's basket + address) is
  // replaced once with an empty basket, then the HubSpot address is
  // filled in once the read lands. Later edits by the rep are kept.
  useEffect(() => {
    if (isPreview) return;
    if (origin.draft !== 'real') {
      setDraft((current) => ({
        ...current,
        quantities: Object.fromEntries(CATALOG_PRODUCTS.map((p) => [p.name, 0])),
        address: { ...BLANK_ADDRESS },
        addressConfirmed: false,
        revision: current.revision + 1,
      }));
      setOrigin({ draft: 'real', address: 'pending' });
      return;
    }
    if (origin.address !== 'pending') return;
    if (identity.status === 'loading') return;
    if (addresses.status === 'ready') {
      if (hubspotAddress) {
        setDraft((current) => sameAddress(current.address, BLANK_ADDRESS)
          ? { ...current, address: hubspotAddress, addressConfirmed: false, revision: current.revision + 1 }
          : current);
        setOrigin({ draft: 'real', address: 'hubspot' });
      } else {
        setOrigin({ draft: 'real', address: 'none' });
      }
    } else if (addresses.status === 'error') {
      setOrigin({ draft: 'real', address: 'error' });
    }
  }, [isPreview, origin, identity.status, addresses.status, hubspotAddress, setDraft, setOrigin]);

  // Never let the fixture draft reach the screen on a real session, even
  // for the render before the effect above runs.
  const realDraftReady = isPreview || origin.draft === 'real';
  const viewQuantities = realDraftReady ? draft.quantities : Object.fromEntries(CATALOG_PRODUCTS.map((p) => [p.name, 0]));
  const viewAddress = realDraftReady ? draft.address : BLANK_ADDRESS;
  const addressStatus: OrderAddressStatus = isPreview ? 'ready'
    : !realDraftReady || origin.address === 'pending' ? (addresses.status === 'error' ? 'error' : 'loading')
      : origin.address === 'error' ? 'error'
        : origin.address === 'none' ? 'none'
          : 'ready';

  const exampleUnitCents = (name: string): number => {
    const entry = EXAMPLE_PRICES[name];
    if (!entry) return 0;
    return Math.round((draft.subscription ? entry.subscription : entry.oneTime) * 100);
  };
  const basket = useMemo(() => CATALOG_PRODUCTS.filter((p) => (viewQuantities[p.name] ?? 0) > 0).map((p) => ({
    sku: isPreview ? EXAMPLE_SKUS[p.name] : p.handle, handle: p.handle, quantity: viewQuantities[p.name] ?? 0,
    exampleUnitCents: isPreview ? exampleUnitCents(p.name) : 0,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  })), [viewQuantities, draft.subscription, isPreview]);
  const contextKey = JSON.stringify([customerId, workId, draft.revision, basket, viewAddress,
    draft.subscription, draft.frequencyDays]);
  const quoteRequest = useMemo<PreviewQuoteRequest>(() => ({
    customerId, workId, contextKey, basket, address: viewAddress,
    subscription: draft.subscription, frequencyDays: draft.frequencyDays,
  }), [customerId, workId, contextKey, basket, viewAddress, draft.subscription, draft.frequencyDays]);
  const quote = quoteResult && validPreviewQuote(quoteResult, quoteRequest) ? quoteResult : null;

  // W36: the pricing engine (real sessions only).
  const purchaseType = purchaseTypeFor(draft.subscription);
  const engineBasket = useMemo(() => basket.map((line) => ({ handle: line.handle, units: line.quantity })), [basket]);
  const engineContextKey = JSON.stringify([customerId, workId, engineBasket, purchaseType, draft.frequencyDays]);
  const engine = useEnginePricing({
    customerId, workId, enabled: !isPreview, purchaseType, cadenceDays: draft.frequencyDays,
    basket: engineBasket, contextKey: engineContextKey,
  });
  const engineReady = !isPreview && engine.catalog.status === 'ready';
  const engineQuote = engine.basket.status === 'ready' && engine.basket.contextKey === engineContextKey ? engine.basket.quote : null;
  const engineSummary: EngineSummary | undefined = isPreview ? undefined : {
    catalogStatus: engine.catalog.status,
    catalogPrices: engine.catalog.prices,
    catalogQuote: engine.catalog.quote,
    basketStatus: engine.basket.status,
    basketQuote: engineQuote,
    errorCode: engine.catalog.errorCode ?? engine.basket.errorCode,
  };
  const tilePrices = useMemo<TilePrices>(() => {
    if (isPreview) {
      return Object.fromEntries(CATALOG_PRODUCTS.map((p) => [p.handle, { unitCents: exampleUnitCents(p.name), caption: 'Example' }]));
    }
    if (engine.catalog.status !== 'ready') return {};
    return Object.fromEntries(CATALOG_PRODUCTS.map((p) => {
      const line = engine.catalog.prices[p.handle];
      return [p.handle, line ? { unitCents: line.unitPrice, caption: line.sku ?? 'SKU pending' } : undefined];
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreview, draft.subscription, engine.catalog.status, engine.catalog.prices]);
  const tierStrip: TierStrip | undefined = isPreview ? undefined
    : engine.catalog.quote
      ? {
        title: tierLabel(engine.catalog.quote.tier),
        body: `Prices from the pricing engine (${tierSourceLabel(engine.catalog.quote)}).`,
        tag: engine.catalog.quote.tier === 'standard' ? 'Standard' : 'Tier pricing',
      }
      : { title: 'Pricing tier pending', body: 'The pricing engine sets the tier once it is connected.', tag: 'Pending' };
  const priceCaption = isPreview ? undefined
    : engineReady ? `${tierLabel(engine.catalog.quote?.tier ?? 'standard')} · Pricing engine · No live order`
      : `${PRICES_PENDING_LABEL} · No live order`;

  // W36: secure-link gate, read only while the real-session dialog is open.
  const secureLinkGate = useSecureLinkGate({ enabled: !isPreview && paymentOpen, headers: engine.headers });

  const paymentContext = JSON.stringify([contextKey, quote?.revision ?? null, quote?.taxCents ?? null, quote?.totalCents ?? null,
    engineQuote?.quoteRevision ?? null]);
  const appliedKey = paymentAgreementKey(paymentContext, payment.method, payment.sample);
  const permissionsReady = isPreview && !!quote && payment.agreement.key === appliedKey && payment.agreement.payment
    && (!draft.subscription || payment.agreement.recurring)
    && (payment.method === 'saved' || !!payment.sample?.complete);
  const sumCents = basket.reduce((sum, line) => sum + line.quantity * line.exampleUnitCents, 0);

  useEffect(() => {
    let current = true;
    setQuoteResult(null);
    setQuoteError(false);
    if (loadPreviewQuote && basket.length > 0) {
      Promise.resolve().then(() => loadPreviewQuote(quoteRequest)).then((result) => {
        if (!current) return;
        if (validPreviewQuote(result, quoteRequest)) setQuoteResult(result);
        else setQuoteError(true);
      }).catch(() => { if (current) setQuoteError(true); });
    }
    return () => { current = false; };
  }, [loadPreviewQuote, quoteRequest, basket.length]);

  useEffect(() => {
    setPayment((current) => current.agreement.key === appliedKey
      ? current : { ...current, agreement: emptyAgreement(appliedKey) });
  }, [appliedKey, setPayment]);

  function adjustQty(name: string, delta: number): void {
    setDraft((current) => {
      const next = Math.max(0, (current.quantities[name] ?? 0) + delta);
      return next === current.quantities[name] ? current : {
        ...current, quantities: { ...current.quantities, [name]: next }, revision: current.revision + 1,
      };
    });
  }

  function saveAddress(): void {
    if (['recipient', 'street', 'city', 'region', 'postal', 'country'].some((key) => !addressDraft[key as keyof ShippingAddress]?.trim())) return;
    const next = Object.fromEntries(Object.entries(addressDraft).map(([key, value]) => [key, value.trim()])) as unknown as ShippingAddress;
    setDraft((current) => JSON.stringify(current.address) === JSON.stringify(next) ? current : {
      ...current, address: next, addressConfirmed: false, revision: current.revision + 1,
    });
    setDialog('closed');
  }

  function processOrder(): void {
    if (!isPreview) return;
    if (!draft.addressConfirmed || basket.length === 0 || !permissionsReady) return;
    setDialog('process');
  }

  async function prepareRealSecureLink(): Promise<void> {
    setPaymentOpen(false);
    setLinkOutcome(null);
    setDialog('secure-link');
    const outcome = await prepareSecureLink({ headers: engine.headers, customerId, workId });
    setLinkOutcome(outcome);
  }

  const addressField = (key: keyof ShippingAddress, label: string): ReactNode => <label key={key}>
    {label}<input value={addressDraft[key] ?? ''} onChange={(event) => setAddressDraft((current) => ({
      ...current, [key]: event.target.value,
    }))} />
  </label>;
  let dialogBody: ReactNode = null;
  let dialogTitle = '';
  if (dialog === 'address') {
    dialogTitle = 'Shipping address';
    dialogBody = <><p>{isPreview
      ? 'This edit applies to the current example order. Customer defaults and subscriptions have their own save scope.'
      : 'This edit applies to this order draft only. The HubSpot address is not changed.'}</p>
      {addressField('recipient', 'Recipient')}{addressField('street', 'Street address')}
      {addressField('line2', 'Address line 2 (optional)')}
      <div className="address-grid">{addressField('city', 'City')}{addressField('region', 'State / region')}</div>
      <div className="address-grid">{addressField('postal', 'Postal code')}{addressField('country', 'Country')}</div></>;
  } else if (dialog === 'process') {
    dialogTitle = 'Process order';
    dialogBody = <><p>{customer.fullName} · {draft.subscription ? 'Subscription every ' + draft.frequencyDays + ' days' : 'One-time purchase'}</p>
      <dl>{CATALOG_PRODUCTS.filter((p) => (viewQuantities[p.name] ?? 0) > 0).map((p) => <div key={p.handle} style={{ display: 'contents' }}>
        <dt>{p.name} × {viewQuantities[p.name]}</dt><dd>{formatCents((viewQuantities[p.name] ?? 0) * exampleUnitCents(p.name))}</dd>
      </div>)}<dt>Products</dt><dd>{formatCents(sumCents)}</dd><dt>Tax · Example</dt><dd>{quote ? formatCents(quote.taxCents) : 'Awaiting Shopify'}</dd>
        <dt>Order total</dt><dd>{quote ? formatCents(quote.totalCents) : 'Awaiting Shopify tax'}</dd></dl>
      <p>Ship to {viewAddress.recipient}, {viewAddress.street}, {viewAddress.city}, {viewAddress.region} {viewAddress.postal}.</p>
      <p>Payment: {paymentLabel(payment)}.</p><div className="caution">Design preview only. No card is submitted or verified and no charge, order or subscription is created. Authoritative pricing, eligibility and provider results must be connected by the runtime.</div></>;
  } else if (dialog === 'secure-link') {
    dialogTitle = 'Prepare secure payment link';
    dialogBody = isPreview
      ? <><p>{firstName} can enter a new card directly with the payment provider through secure checkout.</p>
        <div className="caution">Design preview only. No payment link was created or sent. The uncommitted card draft has been cleared.</div></>
      : <><p>{firstName} can enter a new card directly with the payment provider through secure checkout.</p>
        <p role="status" data-testid="secure-link-outcome">{linkOutcome === null
          ? 'Preparing the secure payment link through the runtime…'
          : linkOutcome.ok
            ? 'The runtime prepared the secure payment link. Send it to the customer on their preferred channel; nothing is charged from this screen.'
            : `No payment link was created or sent (${linkOutcome.result}${linkOutcome.detail ? ': ' + linkOutcome.detail : ''}).`}</p>
        <div className="caution">A prepared link is not a payment. Payment, order and subscription results are recorded separately once the provider reports them.</div></>;
  } else if (dialog === 'massager' || dialog === 'laser') {
    dialogTitle = dialog === 'massager' ? 'EquiMassager' : 'EquiLaser · Backorder review';
    dialogBody = <><p>The runtime supplies confirmed price, stock, shipping and payment terms before this one-time item can be added.</p>
      <div className="caution">Equipment remains separate from the recurring supplement schedule. No confirmed shipment date or charge is supplied in this preview.</div></>;
  }

  // Companion state. The boundary wraps the build-grid; the inline
  // quoteError <p> below retains its original behavior and only
  // flips to boundary error when an explicit error signal arrives
  // from the runtime (feedback_no_fabricated_numbers: never promote
  // the preview-quote mismatch to a system error on this surface).
  //
  // R10: the Kixie mid-call signal arrives via the active-call SSE
  // stream through useActiveCallSignal; the prop-based signal still
  // wins so unit tests remain hermetic.
  const liveMidCall = useActiveCallSignal({
    customerId,
    label: `Call in progress with ${isPreview ? SARAH.firstName : firstName}`,
    actionName: 'KIXIE_CALL_IN_PROGRESS',
  });
  const mergedSignals = useMemo(
    () => mergeMidCallSignal(companionSignals, liveMidCall),
    [companionSignals, liveMidCall],
  );
  const state = useMemo(
    () => resolveCompanionState(mergedSignals),
    [mergedSignals],
  );

  const defaultAddress = isPreview
    ? sameAddress(draft.address, DEFAULT_REVIEW_ADDRESS)
    : !!hubspotAddress && realDraftReady && sameAddress(draft.address, hubspotAddress);

  return <WorkspaceChrome screen="build-order" rail="my-work">
    <div className="breadcrumbs"><div>My work<span className="slash">/</span><strong>{customer.fullName}</strong></div>
      {isPreview ? <span className="preview">Design preview · Sample customer</span>
        : identity.statusPill ? <span className="preview">{identity.statusPill}</span> : null}</div>
    {isPreview ? (
      <PriorityFocus ariaLabel="SALES-ASSISTED ORDER" eyebrow="SALES-ASSISTED ORDER" heading="Build Sarah's next order"
        what={{ label: 'WHAT', heading: 'Build the right basket', iconId: 'message' }}
        whyNow={{ label: 'WHY NOW', body: 'Sarah is ready to order. Confirm products and her preferred schedule.', iconId: 'clock' }}
        nextAction={{ label: 'NEXT ACTION', body: 'Review the basket, delivery details and final quote.', detail: 'Customer agreement follows the quote review.', iconId: 'target' }} />
    ) : (
      <PriorityFocus ariaLabel="SALES-ASSISTED ORDER" eyebrow="SALES-ASSISTED ORDER" heading={`Build ${firstName}'s next order`}
        what={{ label: 'WHAT', heading: 'Build the right basket', iconId: 'message' }}
        whyNow={{ label: 'WHY NOW', body: `${firstName}'s basket starts empty. Confirm products and the preferred schedule on the call.`, iconId: 'clock' }}
        nextAction={{ label: 'NEXT ACTION', body: 'Review the basket, delivery details and final quote.', detail: engineReady
          ? 'Prices come from the pricing engine. Tax, total and the draft order stay pending until the Shopify gate is connected.'
          : 'Prices and the draft order stay pending until the pricing engine and the Shopify gate are connected.', iconId: 'target' }}
        ownership={{ label: 'OWNERSHIP', iconId: 'people', detail: identity.ownerDetail }}
        owner={identity.owner} />
    )}
    <IdentityRow customer={customer} badgeLabel="Order in preparation" sinceLabel={identity.sinceLabel} />
    <FileShortcuts customerId={customerId} firstName={customer.firstName} />
    <CustomerTabs customerId={customerId} active="build-order" />
    <DataBoundary state={state} testId="build-order-boundary">
      <div className="build-grid">
        <ProductWorkspace category={category} catalog={CATALOG_PRODUCTS} quantities={viewQuantities} subscription={draft.subscription}
          frequencyDays={draft.frequencyDays} prices={tilePrices} tierStrip={tierStrip} mode={isPreview ? 'preview' : 'real'}
          pendingPriceLabel={engine.catalog.status === 'loading' ? 'Loading price…' : 'Price pending'}
          onPickCategory={setCategory} onAdjustQty={adjustQty}
          onSetSubscription={(subscription) => setDraft((current) => current.subscription === subscription ? current : {
            ...current, subscription, revision: current.revision + 1,
          })}
          onSetFrequency={(frequencyDays) => setDraft((current) => current.frequencyDays === frequencyDays ? current : {
            ...current, frequencyDays, revision: current.revision + 1,
          })}
          onReviewEquipment={(which) => setDialog(which)}
          customerFirstName={isPreview ? SARAH.firstName : firstName}
          priceCaption={priceCaption} />
        <OrderSummary catalog={CATALOG_PRODUCTS} quantities={viewQuantities} subscription={draft.subscription}
          frequencyDays={draft.frequencyDays} address={viewAddress} addressConfirmed={realDraftReady && draft.addressConfirmed}
          defaultAddress={defaultAddress}
          payment={payment} quote={quote} permissionsReady={permissionsReady}
          customer={{ initials: customer.initials, fullName: customer.fullName }}
          mode={isPreview ? 'preview' : 'real'} addressStatus={addressStatus}
          examplePrices={isPreview ? EXAMPLE_PRICES : undefined} engine={engineSummary} draftOrderGate={DRAFT_ORDER_GATE}
          onOpenAddress={() => { setAddressDraft({ ...viewAddress }); setDialog('address'); }}
          onConfirmAddress={() => setDraft((current) => ({ ...current, addressConfirmed: true }))}
          onChangePayment={() => { if (isPreview || engineReady) setPaymentOpen(true); }} onProcess={processOrder} />
      </div>
      {quoteError ? <p role="status">The example quote could not be matched to this order. Payment confirmations remain unavailable.</p> : null}
    </DataBoundary>
    <UpNext customerId={customerId} />
    <ActionFooter disabled noteWhenDisabled="Order in preparation" noteWhenEnabled="Order result confirmed" summaryBody="Review the order before proceeding to payment." />
    {paymentOpen && (isPreview || engineReady) ? <PaymentDialog applied={payment} orderContextKey={paymentContext}
      totalCents={isPreview ? (quote?.totalCents ?? null) : null}
      subscription={draft.subscription} frequencyDays={draft.frequencyDays}
      hasShippingAddress={[viewAddress.recipient, viewAddress.street, viewAddress.city,
        viewAddress.region, viewAddress.postal, viewAddress.country].every((part) => !!part?.trim())}
      mode={isPreview ? 'preview' : 'real'} customerFirstName={isPreview ? SARAH.firstName : firstName}
      secureLink={isPreview ? undefined : { gate: secureLinkGate, gateName: SECURE_LINK_GATE, onPrepare: () => { void prepareRealSecureLink(); } }}
      onApply={(selection) => { setPayment(selection); setPaymentOpen(false); }} onCancel={() => setPaymentOpen(false)}
      onPrepareLink={() => { setPaymentOpen(false); setDialog('secure-link'); }} /> : null}
    {dialog !== 'closed' ? <OrderDialog open title={dialogTitle} body={dialogBody}
      confirmLabel="Save address" onConfirm={dialog === 'address' ? saveAddress : null} onClose={() => setDialog('closed')} /> : null}
  </WorkspaceChrome>;
}

export default BuildOrderReviewScreen;
